/**
 * Analytics dispatcher shared by the sites.
 *
 * Feature code reports an event once, through `track` (or `trackEcommerce`),
 * and the dispatcher hands it to every sink: the GTM data layer (see
 * data-layer.ts) and each adapter the site registered at boot, e.g. Sentry or
 * a session recorder. Adapters decide which events they handle; GTM ignores
 * events that have no tag.
 */
import { dataLayer, pushEvent } from "utils/data-layer"

export type AnalyticsParams = Record<string, unknown>
export type AnalyticsAdapter = (event: string, params: AnalyticsParams) => void

// Attribution fields written on every push, as undefined when an event has
// none: GTM keeps pushed values for the whole page, so otherwise an event
// would inherit the previous event's attribution.
const ATTRIBUTION_FIELDS = ["touchpoint", "paywall_entry"]

const adapters: AnalyticsAdapter[] = []

export function registerAnalyticsAdapter(adapter: AnalyticsAdapter): void {
  adapters.push(adapter)
}

// `gtmOptions` go to the data layer only, e.g. eventCallback and eventTimeout.
export function track(
  event: string,
  params: AnalyticsParams = {},
  gtmOptions: AnalyticsParams = {},
): void {
  const attribution: AnalyticsParams = {}
  ATTRIBUTION_FIELDS.forEach((field) => {
    attribution[field] = params[field]
  })
  pushEvent(event, { ...attribution, ...params, ...gtmOptions })

  adapters.forEach((adapter) => {
    try {
      adapter(event, params)
    } catch (error) {
      // A failing sink must not break the page or the other sinks.
    }
  })
}

// GA4 ecommerce events: clear the previous ecommerce object first, as Google
// recommends, so items or coupons of an earlier event cannot leak into this one.
export function trackEcommerce(
  event: string,
  ecommerce: AnalyticsParams,
  params: AnalyticsParams = {},
): void {
  dataLayer().push({ ecommerce: null })
  track(event, { ...params, ecommerce })
}

// For events right before a full-page navigation: navigates once GTM reports
// the event as dispatched, or after a timeout when GTM is blocked or absent.
export function trackThenNavigate(
  event: string,
  params: AnalyticsParams,
  navigate: () => void,
): void {
  let navigated = false
  const go = () => {
    if (navigated) return
    navigated = true
    navigate()
  }
  track(event, params, { eventCallback: go, eventTimeout: 500 })
  window.setTimeout(go, 700)
}
